import { useCallback, useEffect, useRef } from 'react';

interface UseTextareaAutoExpandOptions {
  minHeight?: number;
  maxHeight?: number;
  dependencies?: unknown[];
}

export function useTextareaAutoExpand(
  value: string,
  options: UseTextareaAutoExpandOptions = {}
) {
  const { minHeight = 40, maxHeight = 200, dependencies = [] } = options;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height
    const newHeight = Math.min(
      Math.max(textarea.scrollHeight, minHeight),
      maxHeight
    );

    textarea.style.height = `${newHeight}px`;
  }, [minHeight, maxHeight]);

  // Adjust height when value changes
  useEffect(() => {
    adjustHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, adjustHeight, ...dependencies]);

  // Adjust height on mount and when textarea element changes
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Initial adjustment
    adjustHeight();

    // Also adjust on window resize
    const handleResize = () => adjustHeight();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [adjustHeight]);

  return textareaRef;
}
